import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  CardContent,
  Card,
  Radio,
  Grid,
  Chip,
  Switch,
  CircularProgress,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import AdjustIcon from "@mui/icons-material/Adjust";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import type { DocTemplateListItem } from "./templateSerializer";

// Color mapping for template types
const TEMPLATE_TYPE_COLORS: Record<string, { main: string; bg: string }> = {
  "file note": { main: "#26C0E2", bg: "#E9F9FC" },
  email: { main: "#5B93FF", bg: "#EFF4FF" },
  skill: { main: "#8B5CF6", bg: "#F5F0FF" },
  "fact find": { main: "#FF8F6B", bg: "#FFF4F0" },
  soa: { main: "#605BFF", bg: "#EFEFFF" },
  "e-sign": { main: "#656575", bg: "#F4F5F7" },
  powerpoint: { main: "#D1652C", bg: "#FCF0E9" },
};
function getTemplateTypeColors(type: string) {
  return (
    TEMPLATE_TYPE_COLORS[type?.toLowerCase?.()] || {
      main: "#1976d2",
      bg: "#F5F7F9",
    }
  );
}

const TEMPLATE_TYPE_BY_TAB: Record<number, string> = {
  0: "file note",
  1: "fact find",
  2: "powerpoint",
  5: "email",
  6: "skill",
};

function belongsToActiveTab(template: DocTemplateListItem, activeTab: number) {
  const templateType = TEMPLATE_TYPE_BY_TAB[activeTab];
  return !templateType || template.templateType === templateType;
}

interface TemplateListPanelProps {
  list: DocTemplateListItem[];
  selectedIndex: number | null;
  activeTab: number;
  templatesLoading: boolean;
  templateListCollapsed: boolean;
  setTemplateListCollapsed: (updater: (prev: boolean) => boolean) => void;
  isGlobalAdmin: boolean;
  onNewTemplate: () => void;
  onSelectTemplate: (idx: number) => void;
  onViewVersions: (templateId: string) => void;
  onCloneTemplate: (idx: number) => void;
  onDeleteRequest: (templateId: string) => void;
  onToggleGlobalTemplate: (idx: number) => void;
}

const TemplateListPanel = ({
  list,
  selectedIndex,
  activeTab,
  templatesLoading,
  templateListCollapsed,
  setTemplateListCollapsed,
  isGlobalAdmin,
  onNewTemplate,
  onSelectTemplate,
  onViewVersions,
  onCloneTemplate,
  onDeleteRequest,
  onToggleGlobalTemplate,
}: TemplateListPanelProps) => {
  return (
    <Paper
      className="tour-template-list"
      elevation={3}
      sx={{
        // hide panel completely when Agreements / Fact Find New tab is active
        display: activeTab === 3 || activeTab === 4 ? "none" : "flex",
        borderRadius: 3,
        width: {
          xs: "100%",
          sm: "100%",
          md: templateListCollapsed ? "80px" : "20%",
        },
        minWidth: {
          xs: "100%",
          md: templateListCollapsed ? "80px" : "200px",
        },
        maxWidth: {
          xs: "100%",
          md: templateListCollapsed ? "80px" : "20%",
        },
        flex: {
          xs: "none",
          md: "0 0 auto",
        },
        p: 1,
        height: {
          xs: "auto",
          sm: "auto",
          md: "100%",
        },
        minHeight: {
          xs: "auto",
          md: "100%",
        },
        maxHeight: {
          xs: "200px",
          md: "100%",
        },
        flexDirection: "column",
        position: "relative",
        bgcolor: "background.paper",
        transition: "width 0.3s",
        overflow: "hidden",
        mb: { xs: 2, md: 0 },
        py: "20px",
      }}
    >
      {/* Fixed Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          minHeight: 48,
          px: 1,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{
            display: templateListCollapsed ? "none" : "block",
            textWrap: "nowrap",
            fontSize: { xs: "1rem", lg: "1.25rem" },
          }}
        >
          Template List
        </Typography>
        <Stack direction="row" alignItems="center">
          {!templateListCollapsed && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={onNewTemplate}
                sx={{
                  minWidth: 48,
                  px: 2,
                  fontWeight: 600,
                  height: 34,
                  whiteSpace: "nowrap",
                }}
              >
                NEW
              </Button>
            </>
          )}
          <IconButton
            onClick={() => setTemplateListCollapsed((prev) => !prev)}
            sx={{
              display: { xs: "none", lg: "flex" }, // Hide collapse button on mobile
            }}
          >
            {templateListCollapsed ? (
              <KeyboardDoubleArrowRightIcon fontSize="medium" />
            ) : (
              <KeyboardDoubleArrowLeftIcon fontSize="medium" />
            )}
          </IconButton>
        </Stack>
      </Stack>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: { xs: "hidden", lg: "auto" },
          overflowX: { xs: "auto", lg: "hidden" },
          p: 1,
          scrollbarWidth: "none",
          pt: "20px",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {templateListCollapsed ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              pt: 1,
            }}
          >
            {templatesLoading ? (
              // Show loading spinner when switching organizations for all template types
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  pt: 4,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : list && list.length > 0 ? (
              list
                .map((_e, originalIdx) => ({ ..._e, originalIdx }))
                .filter((_e) => belongsToActiveTab(_e, activeTab))
                .map((_e) => (
                  <Box
                    key={_e.originalIdx}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      p: 0,
                      m: 0,
                    }}
                  >
                    <InsertDriveFileIcon
                      sx={{
                        fontSize: 32,
                        color: "#90A4AE",
                        bgcolor: "#F5F7F9",
                        borderRadius: "50%",
                        p: 0.5,
                        mb: 0.5,
                        boxShadow: 0,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "#90A4AE", fontWeight: 500 }}
                    >
                      File...
                    </Typography>
                  </Box>
                ))
            ) : (
              <Typography variant="caption" align="center" color="text.secondary">
                No Templates Uploaded
              </Typography>
            )}
          </Box>
        ) : (
          <Stack spacing={0}>
            {list && list.length > 0 ? (
              <Grid
                container
                spacing={2}
                sx={{
                  flexWrap: { xs: "nowrap", lg: "wrap" },
                  flexDirection: { xs: "row", lg: "row" },
                }}
              >
                {list
                  .map((e, originalIdx) => ({ ...e, originalIdx }))
                  .filter((e) => belongsToActiveTab(e, activeTab))
                  .map((e) => (
                    <Grid
                      item
                      xs="auto"
                      lg={12}
                      key={e.originalIdx}
                      sx={{
                        minWidth: {
                          xs: "280px",
                          sm: "280px",
                          md: "280px",
                          lg: "auto",
                        },
                        maxWidth: {
                          xs: "280px",
                          sm: "280px",
                          md: "280px",
                          lg: "none",
                        },
                      }}
                    >
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          padding: "10px 0px",
                          borderTop: "1px solid",
                          borderBottom: "1px solid",
                          borderLeft: "1px solid",
                          borderRight: "1px solid",
                          borderColor: "divider",
                          "&:hover": {
                            boxShadow: 2,
                            zIndex: 1,
                            transition: "all 0.2s ease-in-out",
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            p: { xs: 0.5, lg: 0.4 },
                            "&:last-child": { pb: { xs: 0.7, lg: 0.7 } },
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {/* Radio and label section */}
                          <Grid container alignItems="center" spacing={{ xs: 0.5, lg: 1 }}>
                            <Grid item xs={2} sm={1.5} lg={2}>
                              <Radio
                                checked={selectedIndex === e.originalIdx}
                                onChange={() => onSelectTemplate(e.originalIdx)}
                                sx={{
                                  p: { xs: 0.3, lg: 0.5 },
                                  mr: { xs: 0.5, lg: 1 },
                                  color: "#1976d2",
                                  "& .MuiSvgIcon-root": {
                                    fontSize: { xs: 20, lg: 24 },
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={5} sm={4} lg={4}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                {(() => {
                                  const type =
                                    list && list.length > 0
                                      ? list[e.originalIdx]?.templateType
                                      : "";
                                  const { main, bg } = getTemplateTypeColors(type);
                                  return (
                                    <Chip
                                      sx={{
                                        borderRadius: 1,
                                        borderColor: bg,
                                        bgcolor: bg,
                                        color: main,
                                        margin: 0,
                                        minHeight: { xs: 12, lg: 14 },
                                        fontSize: { xs: 10, lg: 12 },
                                        height: { xs: 20, lg: 25 },
                                        "& .MuiChip-label": {
                                          padding: 1,
                                        },
                                      }}
                                      label={
                                        type
                                          ? type === "soa"
                                            ? "SOA"
                                            : type.charAt(0).toUpperCase() + type.slice(1)
                                          : "No type"
                                      }
                                    />
                                  );
                                })()}
                                {/* Show default indicator for PowerPoint templates */}
                                {e.templateType === "powerpoint" && e.isDefaultPPT && (
                                  <Chip
                                    sx={{
                                      borderRadius: 1,
                                      bgcolor: "#FFD700",
                                      color: "#000",
                                      margin: 0,
                                      minHeight: { xs: 12, lg: 14 },
                                      fontSize: { xs: 9, lg: 10 },
                                      height: { xs: 18, lg: 22 },
                                      "& .MuiChip-label": {
                                        padding: "2px 4px",
                                      },
                                    }}
                                    label="DEFAULT"
                                  />
                                )}
                              </Box>
                            </Grid>
                            <Grid item xs={1} sx={{ display: { xs: "none", lg: "block" } }}></Grid>
                            <Grid item xs={5} sm={4.5} lg={5} sx={{ textAlign: "right" }}>
                              <Stack direction="row" spacing={{ xs: 0.5, lg: 1 }} justifyContent="flex-end">
                                {/* Hide version history and clone icons for PowerPoint templates */}
                                {e.templateType !== "powerpoint" && (
                                  <>
                                    <Tooltip
                                      title={
                                        // Org admins cannot view versions of templates created by global admin
                                        !isGlobalAdmin && e.templateUploadedBy === "global admin"
                                          ? "Organisation Admin cannot view versions of templates created by Global Admin"
                                          : "View Versions"
                                      }
                                      arrow
                                      placement="top"
                                    >
                                      <span>
                                        <IconButton
                                          color="success"
                                          size="small"
                                          onClick={() => onViewVersions(e.templateId)}
                                          sx={{
                                            p: { xs: "1px", lg: "2px" },
                                            opacity:
                                              !isGlobalAdmin && e.templateUploadedBy === "global admin"
                                                ? 0.5
                                                : 1,
                                          }}
                                          disabled={
                                            !isGlobalAdmin && e.templateUploadedBy === "global admin"
                                          }
                                        >
                                          <AdjustIcon fontSize="inherit" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Clone Template" arrow placement="top">
                                      <span>
                                        <IconButton
                                          sx={{
                                            p: { xs: "1px", lg: "2px" },
                                          }}
                                          onClick={() => onCloneTemplate(e.originalIdx)}
                                          color="primary"
                                          size="small"
                                        >
                                          <ContentCopyIcon fontSize="inherit" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </>
                                )}
                                <Tooltip
                                  title={
                                    !isGlobalAdmin && e.templateUploadedBy === "global admin"
                                      ? "Cannot delete templates created by Global Admin"
                                      : "Delete Template"
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <span>
                                    <IconButton
                                      onClick={() => onDeleteRequest(e.templateId)}
                                      color="error"
                                      size="small"
                                      disabled={
                                        !isGlobalAdmin && e.templateUploadedBy === "global admin"
                                      }
                                      sx={{
                                        opacity:
                                          !isGlobalAdmin && e.templateUploadedBy === "global admin"
                                            ? 0.5
                                            : 1,
                                        p: { xs: "1px", lg: "2px" },
                                      }}
                                    >
                                      <Delete fontSize="inherit" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            </Grid>
                            <Grid item xs={2} sx={{ display: { xs: "none", lg: "block" } }}></Grid>
                            <Grid item xs={10} lg={10}>
                              <Stack
                                direction={{ xs: "column", lg: "row" }}
                                justifyContent={{
                                  xs: "flex-start",
                                  lg: "space-between",
                                }}
                                alignItems={{
                                  xs: "flex-start",
                                  lg: "center",
                                }}
                                gap={0.3}
                                sx={{ mt: { xs: 0.2, lg: 0 } }}
                              >
                                <Chip
                                  sx={{
                                    borderRadius: 1,
                                    bgcolor:
                                      e.templateUploadedBy === "global admin" && !e.isClonedTemplate
                                        ? "#4B729E0D"
                                        : "#4B729E0D",
                                    color: "#4B729E",
                                    borderColor: "#4B729E0D",
                                    borderWidth: 1,
                                    borderStyle: "solid",
                                    fontSize: { xs: 10, lg: 12 },
                                    height: { xs: 16, lg: 20 },
                                    "& .MuiChip-label": {
                                      padding: 0.5,
                                    },
                                  }}
                                  label={
                                    e.templateUploadedBy === "global admin" &&
                                    e.orgId === "0" &&
                                    !e.isClonedTemplate
                                      ? "Seed template"
                                      : "Custom template"
                                  }
                                />
                                {isGlobalAdmin && (
                                  <Switch
                                    size="small"
                                    checked={list[e.originalIdx]?.isGlobalTemplate}
                                    sx={{
                                      transform: {
                                        xs: "scale(0.8)",
                                        lg: "scale(1)",
                                      },
                                    }}
                                    onClick={() => onToggleGlobalTemplate(e.originalIdx)}
                                  />
                                )}
                              </Stack>
                            </Grid>
                            <Grid item xs={2} sx={{ display: { xs: "none", lg: "block" } }}></Grid>
                            <Grid item xs={10} lg={10}>
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.3,
                                  fontSize: { xs: 12, lg: 14 },
                                  lineHeight: { xs: 1.2, lg: 1.4 },
                                  wordBreak: "break-word",
                                }}
                              >
                                {e.templateName}
                              </Typography>
                            </Grid>
                          </Grid>
                          {/* Action icons section */}
                          <Grid
                            container
                            alignItems="center"
                            justifyContent="flex-end"
                            spacing={1}
                            sx={{ flex: 1 }}
                          ></Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            ) : templatesLoading ? (
              // Show loading spinner when switching organizations for all template types
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  pt: 4,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : (
              <Typography variant="body2" align="center" color="text.secondary" sx={{ p: 2 }}>
                No Templates Uploaded
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

export default TemplateListPanel;
